import React, { useEffect, useState } from 'react';

import ConfirmationMessage from '../Controls/ConfirmationMessage';
import warningIcon from '../common/NewImages/i-warning.svg';

import styles from './CssModules/PolicyRowBodyFooter.module.css';

import deleteIcon from '../common/Images/delete.png';
import SaveAsTemplateModal from './components/SaveAsTemplateModal';
import { buildCustomTemplate, getUpdatePolicyTemplateBodyFromPolicyBodyData } from './utils/utils';
import { useCreatePolicyTemplate, useUpdatePolicyTemplate } from './api';
import { useSnackAlert } from './hooks/useSnackAlert';
import { Icons, SnackAlert, SnackAlertContent, Tooltip } from '@silverfort/sf-components';
import { useHistory } from 'react-router-dom';

const SINGLE_USER = 1;

const SNACKALERT_TEXT_CLASSNAME = 'font-sf leading-[15.51px]';
const SNACKALERT_STRONG_TEXT_CLASSNAME = 'font-sf font-medium leading-[15.87px]';

const FOOTER_MESSAGE_DEFAULT_VALUE = { data: '', error: false };

const PolicyRowBodyFooter = ({
    validatePolicyData,
    setStateAndSavePolicy,
    handleDiscardChanges,
    handleDeletePolicy,
    policyBodyData,
    isDirty,
    isCreateFromLogs,
    isCreateFromTemplate,
    onEditTemplate,
    templateToEdit = undefined
}) => {
    const history = useHistory();

    const [footerMsg, setFooterMsg] = useState(FOOTER_MESSAGE_DEFAULT_VALUE);
    const [isDuringSave, setIsDuringSave] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [showDeletePolicyModal, setShowDeletePolicyModal] = useState(false);
    const [showStateChangeModal, setShowStateChangeModal] = useState(false);
    const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState(false);

    const { isOpen: snackAlertOpen, snackAlertContent, popSnackAlert, dismissSnackAlert } = useSnackAlert();

    const handleSaveAsTemplateSuccess = () => {
        setShowSaveAsTemplateModal(false);
        popSnackAlert({
            iconLeft: Icons.CircleCheck,
            message: (
                <span className={SNACKALERT_TEXT_CLASSNAME}>
                    <span className={SNACKALERT_STRONG_TEXT_CLASSNAME}>{policyBodyData.name}</span> template created
                    successfully
                </span>
            ),
            actionMessage: 'Go to Custom Templates',
            onAction: () => {
                history.push('/Policy/Templates/custom');
            }
        });
    };

    const handleSaveAsTemplateError = () => {
        setShowSaveAsTemplateModal(false);
        popSnackAlert({
            iconLeft: Icons.CircleError,
            message: (
                <span className={SNACKALERT_TEXT_CLASSNAME}>
                    <span className={SNACKALERT_STRONG_TEXT_CLASSNAME}>{policyBodyData.name}</span> template creation
                    failed
                </span>
            )
        });
    };

    const handleEditTemplateSuccess = () => {
        popSnackAlert({
            iconLeft: Icons.CircleCheck,
            message: (
                <span className={SNACKALERT_TEXT_CLASSNAME}>
                    <span className={SNACKALERT_STRONG_TEXT_CLASSNAME}>{policyBodyData.name}</span> template was updated
                    successfully
                </span>
            ),
            actionMessage: 'Go to Custom Templates',
            onAction: () => {
                history.push('/Policy/Templates/custom');
            }
        });

        onEditTemplate();
    };

    const handleEditTemplateError = () => {
        setShowSaveAsTemplateModal(false);
        popSnackAlert({
            iconLeft: Icons.CircleError,
            message: (
                <span className={SNACKALERT_TEXT_CLASSNAME}>
                    <span className={SNACKALERT_STRONG_TEXT_CLASSNAME}>{policyBodyData.name}</span> template update
                    failed
                </span>
            )
        });
    };

    const { createPolicyTemplate } = useCreatePolicyTemplate({
        onSuccess: handleSaveAsTemplateSuccess,
        onError: handleSaveAsTemplateError
    });
    const { updatePolicyTemplate } = useUpdatePolicyTemplate({
        onSuccess: handleEditTemplateSuccess,
        onError: handleEditTemplateError
    });

    useEffect(() => {
        if (footerMsg) {
            const timer = setTimeout(() => setFooterMsg({ data: '', error: false }), 5000);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [footerMsg]);

    const handleSaveAsTemplateClicked = () => {
        const isPolicyValid = validatePolicyData();

        if (isPolicyValid) {
            setShowSaveAsTemplateModal(true);
            setFooterMsg(FOOTER_MESSAGE_DEFAULT_VALUE);
        } else {
            setFooterMsg({ data: 'Validation error', error: true });
        }
    };

    const savePolicyAsTemplate = async (description) => {
        const template = buildCustomTemplate(policyBodyData, description);
        await createPolicyTemplate(template);
    };

    const updateTemplate = async () => {
        const updateTemplateDetailsBody = getUpdatePolicyTemplateBodyFromPolicyBodyData(policyBodyData);
        await updatePolicyTemplate(templateToEdit, updateTemplateDetailsBody);
    };

    const onSavePolicyClicked = async () => {
        setIsDuringSave(true);
        const isNewPolicy = policyBodyData.id < 0 || isNaN(policyBodyData.id);
        const isValid = validatePolicyData();
        if (isValid) {
            if (isNewPolicy) {
                setShowStateChangeModal(true);
                return;
            }
            const footerMsg = await setStateAndSavePolicy();
            setIsLoading(false);
            if (!footerMsg.error && footerMsg.data === '') {
                return;
            }
            setFooterMsg(footerMsg);
            setIsDuringSave(false);
        } else {
            setFooterMsg({ data: 'Validation error', error: true });
            setIsDuringSave(false);
            setIsLoading(false);
        }
    };

    const onDeletePolicyClicked = async () => {
        setIsLoading(true);
        const deletionResults = await handleDeletePolicy(policyBodyData.id);
        if (!deletionResults) {
            setFooterMsg({ data: 'Failed to delete policy.', error: true });
            setShowDeletePolicyModal(false);
        }
    };

    const onChangePolicyStateModalClosed = async (policyDesiredState) => {
        setIsLoading(true);
        const footerMsg = await setStateAndSavePolicy(policyDesiredState);
        if (!footerMsg.error && footerMsg.data === '') {
            return;
        }
        setFooterMsg(footerMsg);
        setIsDuringSave(false);
        setIsLoading(false);
        setShowStateChangeModal(false);
    };

    const onHidePolicyStateModal = () => {
        setShowStateChangeModal(false);
        setIsDuringSave(false);
    };

    return (
        <>
            <div className={styles.policyFooter}>
                <div className={styles.footerLeftSide}>
                    {(isDirty || isCreateFromLogs || isCreateFromTemplate) && (
                        <div className="flex">
                            <div>
                                <img className="pr-2 pt-1.5" src={warningIcon} alt="" />
                            </div>
                            <div>
                                {`This policy will apply to ${policyBodyData.users_sum.toLocaleString()} ${
                                    policyBodyData.users_sum === SINGLE_USER ? 'user' : 'users'
                                }`}
                            </div>
                        </div>
                    )}
                </div>
                <div className={styles.footerRightSide}>
                    <div
                        className={`${styles.footerButton} ${styles.footerMsg}  ${
                            footerMsg.error ? styles.errorMsg : ''
                        }`}>
                        {footerMsg.data}
                    </div>
                    <img
                        className={`${styles.footerButton} ${styles.deletePolicyButton}`}
                        src={deleteIcon}
                        onClick={() => {
                            setShowDeletePolicyModal(true);
                        }}
                        alt=""
                    />

                    {policyBodyData.isValid && (
                        <>
                            <button
                                className={`${styles.footerButton} ${styles.discardButton}`}
                                onClick={handleDiscardChanges}
                                disabled={!(isDirty || isCreateFromTemplate) || isDuringSave}>
                                DISCARD CHANGES
                            </button>
                            {!!templateToEdit ? (
                                <Tooltip
                                    className="w-none"
                                    width="262px"
                                    title="This action updates the template, but does not create a new policy.">
                                    <button
                                        className={`${styles.footerButton} ${styles.secondaryButton}`}
                                        onClick={updateTemplate}
                                        disabled={!isDirty || isDuringSave}>
                                        UPDATE TEMPLATE
                                    </button>
                                </Tooltip>
                            ) : (
                                <>
                                    <button
                                        className={`${styles.footerButton} ${styles.secondaryButton}`}
                                        onClick={handleSaveAsTemplateClicked}
                                        disabled={isDuringSave}>
                                        SAVE AS TEMPLATE
                                    </button>
                                    <button
                                        className={`${styles.footerButton} ${styles.saveButton}`}
                                        onClick={onSavePolicyClicked}
                                        disabled={
                                            (!(isDirty || isCreateFromTemplate) && !isCreateFromLogs) || isDuringSave
                                        }>
                                        SAVE
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {showStateChangeModal && (
                <ConfirmationMessage
                    title={'Change Status'}
                    body={`Do you want to turn "${policyBodyData.name}" 'ON'?`}
                    approveText={'YES'}
                    cancelText={'NO'}
                    loading={isLoading}
                    loadingText={''}
                    OnCancel={() => onChangePolicyStateModalClosed(false)}
                    OnApprove={() => onChangePolicyStateModalClosed(true)}
                    OnHide={onHidePolicyStateModal}
                    disableCloseButton={isLoading}
                />
            )}

            {showDeletePolicyModal && (
                <ConfirmationMessage
                    title={'Delete Policy'}
                    body={`Are you sure you want to delete "${policyBodyData.name}" ?`}
                    approveText={'Delete'}
                    cancelText={'Cancel'}
                    approveStyle={'danger'}
                    loading={isLoading}
                    loadingText={''}
                    OnCancel={() => setShowDeletePolicyModal(false)}
                    OnApprove={onDeletePolicyClicked}
                />
            )}

            <SaveAsTemplateModal
                open={showSaveAsTemplateModal}
                policyName={policyBodyData.name}
                onSaveAsTemplate={savePolicyAsTemplate}
                onClose={() => setShowSaveAsTemplateModal(false)}
            />

            <SnackAlert
                open={snackAlertOpen}
                autoHideDuration={2000}
                onClose={dismissSnackAlert}
                className="absolute"
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}>
                <SnackAlertContent {...snackAlertContent} />
            </SnackAlert>
        </>
    );
};

export default PolicyRowBodyFooter;
